import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Moon, RotateCcw, Sun, X } from 'lucide-react';
import { COLOR_PRESETS } from './appSettings';
import { useAppSettings } from './SettingsContext';

function Section({ title, children }) {
  return (
    <Stack spacing={1}>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}
      >
        {title}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
        {children}
      </Stack>
    </Stack>
  );
}

function OptionChip({ label, selected, onClick, icon }) {
  const theme = useTheme();

  return (
    <Chip
      icon={icon}
      label={label}
      onClick={onClick}
      clickable
      sx={{
        borderRadius: '12px',
        border: '2px solid transparent',
        borderStyle: 'solid',
        fontWeight: 500,
        color: 'text.primary',
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : 'grey.100',
        '& .MuiChip-icon': { color: 'inherit' },
        '&:hover': {
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : 'grey.200',
        },
        ...(selected && {
          borderColor: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
        }),
      }}
    />
  );
}

export default function SettingsDrawer() {
  const { settings, drawerOpen, closeDrawer, updateSettings, resetSettings, isCustom } = useAppSettings();

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={closeDrawer}
      disableScrollLock
      ModalProps={{
        BackdropProps: { invisible: true },
        sx: { zIndex: 1400 },
      }}
      PaperProps={{
        elevation: 24,
        sx: {
          width: { xs: '100%', sm: 440 },
          maxWidth: '100%',
          bgcolor: 'background.paper',
          color: 'text.primary',
        },
      }}
    >
      <Stack sx={{ height: '100%', overflow: 'auto' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2, pb: 1 }}
        >
          <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
            App Settings
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              color="inherit"
              onClick={resetSettings}
              aria-label="Reset settings"
              size="small"
              sx={{ opacity: isCustom ? 1 : 0.35 }}
            >
              <RotateCcw size={18} />
            </IconButton>
            <IconButton color="inherit" onClick={closeDrawer} aria-label="Close settings" size="small">
              <X size={20} />
            </IconButton>
          </Stack>
        </Stack>

        <Stack spacing={5} sx={{ p: 3, pb: 4 }}>
          <Section title="Primary Color">
            {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
              <OptionChip
                key={key}
                label={preset.label}
                selected={settings.colorPreset === key}
                onClick={() => updateSettings({ colorPreset: key })}
                icon={
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: preset.primary,
                      ml: 0.5,
                    }}
                  />
                }
              />
            ))}
          </Section>

          <Section title="Color Mode">
            <OptionChip
              label="Light"
              selected={settings.paletteMode === 'light'}
              onClick={() => updateSettings({ paletteMode: 'light' })}
              icon={<Sun size={16} style={{ marginLeft: 8 }} />}
            />
            <OptionChip
              label="Dark"
              selected={settings.paletteMode === 'dark'}
              onClick={() => updateSettings({ paletteMode: 'dark' })}
              icon={<Moon size={16} style={{ marginLeft: 8 }} />}
            />
          </Section>

          <Section title="Nav Color">
            {[
              { label: 'Blend-in', value: 'blend-in' },
              { label: 'Discrete', value: 'discrete' },
              { label: 'Evident', value: 'evident' },
            ].map((opt) => (
              <OptionChip
                key={opt.value}
                label={opt.label}
                selected={settings.navColor === opt.value}
                onClick={() => updateSettings({ navColor: opt.value })}
              />
            ))}
          </Section>

          <Section title="Contrast">
            <OptionChip
              label="High"
              selected={settings.contrast === 'high'}
              onClick={() => updateSettings({ contrast: 'high' })}
            />
            <OptionChip
              label="Normal"
              selected={settings.contrast === 'normal'}
              onClick={() => updateSettings({ contrast: 'normal' })}
            />
          </Section>
        </Stack>
      </Stack>
    </Drawer>
  );
}
